import { graphqlOperation } from "@aws-amplify/api";

import useAPI from "@toothfairy/shared-api/useApi";
import AppQuantumEditorUtils from "@toothfairy/shared-ui/AppQuantumEditorUtils";
import { useTranslation } from "react-i18next";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import { useCallback, useEffect, useState } from "react";
import { isConnectedToCloud, reservedConfigs, userType } from "../../App";
import envConfig from "../../envConfig";
import { makeGraphQLRequest } from "../API";
import Agents from "../API/Agents";
import chatsHelper from "../API/Chats/chatsHelper";
import Generator from "../API/Generator";
import Prompts from "../API/Prompts";
import Constants from "../Constants";
import { ENABLE_CONSUMPTION_BILLING } from "../Constants/billing";
import { useHistory } from "../Router";
import { generateRowId } from "../Utility";
import { getSubscriptionBaseConfig } from "../graphql/queries";
import { Chat, UserType } from "../models";

const useWorkspaces = () => {
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const history = useHistory();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [loadMoreChatsToken, setLoadMoreChatsToken] = useState(null);
  const [isChatHistoryLoaded, setIsChatHistoryLoaded] = useState(false);
  const [isWorkspaceEnabled, setIsWorkspaceEnabled] = useState(true);
  const {
    loading: generatorConfigInProgress,
    apiRequest: generatorConfigRequest,
  } = useAPI(Generator.getConfig, envConfig, true);

  const {
    loading: generatorUpdateConfigInProgress,
    apiRequest: generatorUpdateConfigRequest,
  } = useAPI(Generator.updateConfig, envConfig, true);

  const isWorkspaceDisabled = (workspace) => {
    const extraDay = 24 * 60 * 60 * 1000;
    return (
      workspace?.activeUntil &&
      new Date(workspace?.activeUntil).getTime() + extraDay < Date.now()
    );
  };
  const isWorkspaceInCreation = (workspace) => {
    return !workspace?.activeUntil;
  };

  const returnCleanedWorkspaces = useCallback((workspaces, includeDisabled) => {
    return workspaces
      ?.sort(AppUtilities.sortBy("title"))
      ?.filter((u) =>
        includeDisabled
          ? u
          : isWorkspaceDisabled(u) === false ||
            isWorkspaceInCreation(u) === true
      );
  }, []);
  const getUserName = (id) => {
    return state?.workspaceUsers
      ? state?.workspaceUsers[
          state?.workspaceUsers.map((e) => e?.id).indexOf(id)
        ]?.username
      : "";
  };
  const canUpdateSubscription = () => {
    let canUpdateSubscription = true;
    if (getActiveWorkspace()?.lastChangeSubscriptionDate) {
      // Creating date object from the ISO string
      let isoDate = new Date(getActiveWorkspace()?.lastChangeSubscriptionDate);
      // Getting the current date
      let currentDate = new Date();
      // Comparing the months
      if (currentDate.getUTCMonth() === isoDate.getUTCMonth()) {
        canUpdateSubscription = false;
      }
    }
    return canUpdateSubscription;
  };
  const getDisabledWorkspaces = (workspaces) => {
    return workspaces?.filter(
      (u) =>
        isWorkspaceDisabled(u) === true && isWorkspaceInCreation(u) === false
    );
  };
  const getWorkspaceUsers = useCallback(() => {
    return state?.workspaceUsers;
  }, [state?.activeWorkspaceID, state?.workspaceUsers]);
  const handleStateUpdateFromSubMessage = (msg, stateProperty, dispatchFn) => {
    const objectIndex = state[stateProperty]?.findIndex(
      (log) => log.id === msg?.element?.id
    );
    switch (msg?.opType) {
      case "UPDATE":
        if (objectIndex >= 0) {
          const updatedProperty = [...state[stateProperty]];
          updatedProperty[objectIndex] = msg?.element;
          dispatch(dispatchFn, updatedProperty);
        }
        break;
      case "INSERT":
        dispatch(dispatchFn, [...state[stateProperty], msg?.element]);
        break;
      case "DELETE":
        if (objectIndex >= 0) {
          state[stateProperty].splice(objectIndex, 1);
          dispatch(dispatchFn, [...state[stateProperty]]);
        }
        break;
      default:
        break;
    }
  };
  const getActiveWorkspace = () => {
    return state?.workspaces?.filter(
      (u) => u?.id === state?.activeWorkspaceID
    )[0];
  };
  // Consumption model: "pro"/"base" are deprecated. For presentation purposes
  // (feature gating only) they resolve to "business" so all legacy individual
  // workspaces keep full feature access under the new Business tier.
  const getEffectiveSubscriptionType = (workspace) => {
    const type = (workspace?.subscriptionType || "").toLowerCase();
    if (!ENABLE_CONSUMPTION_BILLING) return type;
    if (type === "pro" || type === "base") return "business";
    return type;
  };
  const getContentMultiplier = () => {
    return getActiveWorkspace()?.gnUrl?.includes("curie") ? 1 : 2;
  };
  useEffect(() => {
    let updateAppLogSubscription;
    const setupSubscription = async () => {
      if (getActiveWorkspace()?.id) {
        // Subscription for updated app logs
        // RE-ENABLE WHEN ALL IS COMPLETED
        // updateAppLogSubscription = await subscribeGraphQL(
        //   graphqlOperation(onUpdateAppLogSocket, {
        //     filter: {
        //       and: [
        //         { workspaceID: { eq: getActiveWorkspace()?.id } },
        //         {
        //           or: [
        //             { status: { eq: LogStatus.IN_PROGRESS } },
        //             { status: { eq: LogStatus.DISPATCHED } },
        //           ],
        //         },
        //       ],
        //     },
        //   }),
        //   {
        //     next: ({ value }) => {
        //       const updatedAppLog = value.data.onUpdateAppLogEvent;
        //       handleStateUpdateFromSubMessage(
        //         { opType: "UPDATE", element: updatedAppLog },
        //         "appLogs",
        //         "SET_APP_LOGS"
        //       );
        //       notifyAppLog(updatedAppLog);
        //     },
        //     error: (error) =>
        //       console.log("Update AppLog Subscription error:", error),
        //   }
        // );
      }
    };
    setupSubscription();
    return () => {
      if (updateAppLogSubscription) updateAppLogSubscription?.unsubscribe();
    };
  }, [getActiveWorkspace()?.id]);
  useEffect(() => {
    if (getActiveWorkspace()) {
      setIsWorkspaceEnabled(_isWorkspaceEnabled());
    }
  }, [getActiveWorkspace()]);

  const getAgent = (id) => {
    return state?.agents?.find((r) => r?.id === id);
  };
  const getAgents = () => {
    return state?.agents;
  };
  const notifyAppLog = (appLog) => {
    if (
      appLog?.type === "import" &&
      appLog?.status === "completed" &&
      state.appLogs.find((log) => log.id === appLog.id).status !== "completed"
    )
      AppToasty.show(t("toasts.documentImportCompleted"), { type: "success" });
    else if (
      appLog?.type === "import" &&
      appLog?.status === "inError" &&
      state.appLogs.find((log) => log.id === appLog.id).status !== "inError"
    )
      AppToasty.show(t("toasts.documentImportFailed"), { type: "danger" });
    if (
      appLog?.type === "documentImport" &&
      appLog?.status === "inError" &&
      state.appLogs.find((log) => log.id === appLog.id)?.status !== "inError"
    )
      AppToasty.show(t("toasts.documentImportFailed"), { type: "danger" });
    else if (
      appLog?.type === "documentImport" &&
      appLog?.status === "completed" &&
      state.appLogs.find((log) => log.id === appLog.id)?.status !==
        "completed" &&
      appLog.event
    ) {
      AppToasty.show(t("toasts.documentImportCompleted"), { type: "success" });
      dispatch("SET_REDIRECT_TO_DOC", JSON.parse(appLog.event));
    }
  };
  const isTrainingLogIdActive = (id) => {
    if (id == null || id == undefined || id === "") return false;
    return (
      getActiveWorkspace()?.nlpUrl?.includes(id) ||
      getActiveWorkspace()?.qaUrl?.includes(id) ||
      state?.trainingLogs.find(
        (log) =>
          log?.id === id &&
          log?.type === "generativeTraining" &&
          log?.trainingMeta?.model_id?.length > 0
      )?.trainingMeta?.model_id === getActiveWorkspace()?.gnUrl
    );
  };
  const isBatchEnabled = () => {
    return state?.workspacePlan?.isBatchJobsEnabled;
  };
  const isModelDownloadEnabled = () => {
    return state?.workspacePlan?.isDownloadModelEnabled;
  };
  const getMaxUsers = () => {
    const subscriptionType = getActiveWorkspace()?.subscriptionType;
    if (subscriptionType === "business") return 500;
    else if (subscriptionType === "enterprise") {
      return 5000;
    }
    return state?.workspacePlan?.maxUsers;
  };
  const getMaxPowerUsers = () => {
    const subscriptionType = getActiveWorkspace()?.subscriptionType;
    if (subscriptionType === "business") return 10;
    else if (subscriptionType === "enterprise")
      return getActiveWorkspace()?.customMaxPowerUsers || 50;
    else {
      return state?.workspacePlan?.maxUsers;
    }
  };
  const isPowerUserType = (userType) => {
    return [UserType.ROOT, UserType.SUPER_ADMIN, UserType.AI_ENGINEER].includes(
      userType
    );
  };
  const getPowerUsersCount = (usersAccess) => {
    if (!usersAccess) return 0;
    return usersAccess.filter(
      (u) => u?._deleted !== true && isPowerUserType(u?.userType)
    ).length;
  };
  const getRegularUsersCount = (usersAccess) => {
    if (!usersAccess) return 0;
    return usersAccess.filter(
      (u) => u?._deleted !== true && !isPowerUserType(u?.userType)
    ).length;
  };
  const canAddUserOfType = (userType, usersAccess) => {
    if (isPowerUserType(userType)) {
      return getPowerUsersCount(usersAccess) < getMaxPowerUsers();
    } else {
      return getRegularUsersCount(usersAccess) < getMaxUsers();
    }
  };
  const isWorkspaceOnDemand = () => {
    return (
      state?.workspacePlan?.isPredictionOnDemandGlobal &&
      state?.workspacePlan?.isTrainingOnDemandGlobal
    );
  };
  const isPriorityTrainingEnabled = () => {
    return state?.workspacePlan?.isPriorityTrainingEnabled;
  };
  const isTF_API_AccessEnabled = () => {
    return state?.workspacePlan?.isAPIAccessEnabled;
  };
  const getActiveTrainingLog = (type) => {
    if (type === "nlp")
      return state?.trainingLogs?.find((log) =>
        getActiveWorkspace()?.nlpUrl?.includes(log.id)
      );
    else if (type === "qa")
      return state?.trainingLogs?.find((log) =>
        getActiveWorkspace()?.qaUrl?.includes(log.id)
      );
    else if (type === "gn")
      return state?.trainingLogs?.find(
        (log) => log?.trainingMeta?.model_id == getActiveWorkspace()?.gnUrl
      );
  };
  const getFinetunedModels = () => {
    const functionCallingModels = [
      "meta-llama/Llama-3.3-70B-Instruct-Reference",
      "meta-llama/Meta-Llama-3.1-70B-Instruct-Reference",
      "meta-llama/Meta-Llama-3.1-8B-Instruct-Reference",
      "Qwen/Qwen2.5-72B-Instruct",
    ];
    const serverlessModels = [
      "meta-llama/Meta-Llama-3.1-70B-Instruct-Reference",
      "meta-llama/Meta-Llama-3.1-8B-Instruct-Reference",
      "Qwen/Qwen2.5-14B-Instruct",
      "Qwen/Qwen2.5-72B-Instruct",
    ];
    // New-pipeline fine-tunes (provider "toothfairyai") are served by the
    // models_list registry entries (ToothFairyAI/… ids with tier pricing) —
    // the S3-path trainingMeta.model_id is NOT resolvable by the chat stack,
    // so those models must NOT be offered from here (they'd silently fall
    // back to a base model and mis-price UoI). Legacy serverless completions
    // keep flowing through this list.
    const completed_jobs = state?.trainingLogs
      ?.filter((u) => u?.status == "completed" && u?.trainingMeta)
      ?.map((u) => {
        if (typeof u?.trainingMeta == "string") {
          u.trainingMeta = JSON.parse(u?.trainingMeta);
        }
        // Support both new format (model_id) and legacy (t_model_id)
        if (u?.trainingMeta?.model_id || u?.trainingMeta?.t_model_id) return u;
        return undefined;
      })
      .filter((u) => u);

    const models = completed_jobs?.map((u) => {
      const modelId = u?.trainingMeta?.model_id || u?.trainingMeta?.t_model_id;
      const baseModel =
        u?.trainingMeta?.base_model || u?.trainingMeta?.t_status?.model;
      const isNewPipeline = u?.trainingMeta?.provider === "toothfairyai";
      if (isNewPipeline) return undefined;
      if (
        serverlessModels.includes(u?.trainingMeta?.t_status?.model) ||
        getActiveWorkspace()?.enableExperimentalFeatures
      )
        return {
          id: modelId,
          // Show the user-given training name — the raw model id
          // is meaningless in dropdowns.
          name: u?.name || modelId,
          tfOnly: true,
          plannerAvailable: functionCallingModels.includes(baseModel),
          preventFunctionCalling: !functionCallingModels.includes(baseModel),
          dynamicRouting: true,
        };
      return undefined;
    });
    return models?.filter((u) => u);
  };

  const getLastWorkspaceModelMeta = () => {
    return state?.trainingLogs
      ?.sort((a, b) => (a?._lastChangedAt < b?._lastChangedAt ? 1 : -1))
      ?.filter((u) => {
        return (
          u?.status === "completed" &&
          u?.type === "nlpTraining" &&
          u?.trainingConfig?.datasetOnly !== true
        );
      })[0]?.trainingMeta;
  };
  const getLastWorkspaceReadingComprehensionMeta = () => {
    return state?.trainingLogs
      ?.sort((a, b) => (a?._lastChangedAt < b?._lastChangedAt ? 1 : -1))
      ?.filter(
        (u) =>
          u?.status === "completed" &&
          u?.type === "readComprehensionTraining" &&
          u?.trainingConfig?.datasetOnly !== true
      )[0]?.trainingMeta;
  };
  const getLastCompletedTrainingLog = () => {
    return state?.trainingLogs
      ?.filter((u) => u.status === "completed")
      ?.sort((a, b) => (a?._lastChangedAt < b?._lastChangedAt ? 1 : -1))[0];
  };
  const updateWorkspaceInContext = (workspace) => {
    const idx = state?.workspaces
      ?.map((i) => i?.id)
      ?.indexOf(state?.activeWorkspaceID);
    if (idx != -1) {
      state.workspaces[idx] = workspace;
      dispatch("SET_WORKSPACES", [...state.workspaces]);
    }
  };
  const mapModeWithLabel = (mode) => {
    switch (mode) {
      case "planner":
        return "Orchestrator";
      case "chatter":
        return "Assistant";
      case "coder":
        return "Programmer";
      case "retriever":
        return "Operator";
      case "computer":
        return "Desktop";
      case "voice":
        return "Voice";
      case "workspace":
        return "Workspace";
      default:
        return "Unknown";
    }
  };
  const isNLPModelTrained = () => {
    return (
      state?.trainingLogs?.filter((u) => u?.type === "nlpTraining")?.length > 0
    );
  };
  const isRCModelTrained = () => {
    return (
      state?.trainingLogs?.filter(
        (u) => u?.type === "readComprehensionTraining"
      )?.length > 0
    );
  };
  const isGNModelTrained = () => {
    return (
      state?.trainingLogs?.filter((u) => u?.type === "generativeTraining")
        ?.length > 0
    );
  };
  const getWorkspaceBaseModel = () => {
    return state?.workspaces?.filter(
      (u) => u?.id === state?.activeWorkspaceID
    )[0].baseModel;
  };
  const getWorkspaceEntities = (type) => {
    const entities =
      state?.aiEntitiesConfig?.filter((i) => (type ? i.type === type : true)) ||
      [];
    entities.sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
    );
    return entities;
  };
  const getAppLogs = (type, inProgressOnly = true, status) => {
    let appLogs = state?.appLogs?.filter((i) => i.type === type) || [];
    if (status) {
      appLogs = appLogs?.filter((i) => i.status === status);
    } else if (inProgressOnly) {
      appLogs = appLogs?.filter(
        (i) => i.status === "inProgress" || i.status === "dispatched"
      );
    }
    appLogs.sort((a, b) => (a._lastChangedAt > b._lastChangedAt ? 1 : -1));
    return appLogs;
  };
  const handleTabSelection = (route, routes, screen) => {
    const indexToUpdateInAppTabSelection = state?.appTabSelection
      .map((u) => u.key)
      .indexOf(screen);
    state.appTabSelection[indexToUpdateInAppTabSelection].lastIndex = routes
      .map((x) => x?.key)
      .indexOf(route?.key);
    dispatch("SET_TAB_SELECTION", [...state.appTabSelection]);
    if (getActiveWorkspace()?.id)
      history.push(
        `/workspaces/${getActiveWorkspace()?.id}/${screen}/${route?.routeId}`
      );
  };
  const handleSectionSelection = (section, screen) => {
    const indexToUpdateInAppTabSelection = state?.appTabSelection
      .map((u) => u.key)
      .indexOf(screen);
    state.appTabSelection[indexToUpdateInAppTabSelection].lastIndex = section;
    dispatch("SET_TAB_SELECTION", [...state.appTabSelection]);
  };
  const getLastTabSelectedByScreen = (screen, fallback = 0) => {
    const match = state?.appTabSelection.filter((x) => x.key === screen)[0]
      ?.lastIndex;
    if (match) return match;
    else return fallback;
  };
  const getLogicalTabSelection = (routes, screen) => {
    const match = state?.appTabSelection.filter((x) => x.key === screen)[0]
      ?.lastIndex;
    return routes[match]?.key;
  };
  const getRawTabSelection = (routes, screen) => {
    const match = state?.appTabSelection.filter((x) => x.key === screen)[0]
      ?.lastIndex;
    return routes[match];
  };
  const _isWorkspaceEnabled = () => {
    const extraDay = 24 * 60 * 60 * 1000 * 2; // 48 hours in milliseconds
    const activeWorkspace = getActiveWorkspace();

    return (
      activeWorkspace?.disabled !== true &&
      activeWorkspace?.activeUntil &&
      new Date(activeWorkspace.activeUntil).getTime() + extraDay > Date.now()
    );
  };
  const isUserAdmin = () => {
    return (
      !userType() ||
      userType() === UserType.ROOT ||
      userType() === UserType.SUPER_ADMIN
    );
  };
  const isUserRoot = () => {
    return (
      user.id &&
      getActiveWorkspace()?.userID &&
      user.id === getActiveWorkspace()?.userID &&
      userType() === UserType.ROOT
    );
  };
  const isUserContentAdmin = () => {
    return (
      isUserRoot() ||
      userType() === UserType.CONTENT_ADMIN ||
      isUserAdmin() ||
      isUserAIEngineer()
    );
  };
  const isUserAIEngineer = () => {
    return isUserRoot() || userType() === UserType.AI_ENGINEER || isUserAdmin();
  };
  const isToothFairyStaff = () => {
    const email = user?.user_email?.toLowerCase();
    return (
      (isUserAdmin() || isUserAIEngineer()) &&
      email?.endsWith("@toothfairyai.com")
    );
  };
  const isUserLoadedInWorkspace = () => {
    return user.id && getActiveWorkspace()?.userID;
  };
  const getTrainingLogInfo = (id) => {
    return state?.trainingLogs?.filter((i) => i?.id === id)[0];
  };
  const getBatchLogInfo = (id) => {
    let _obj = state?.batchLogs?.find((i) => i?.id === id);
    if (typeof _obj?.batchConfig == "string")
      _obj.batchConfig = JSON.parse(_obj.batchConfig);
    if (typeof _obj?.batchMeta == "string")
      _obj.batchMeta = JSON.parse(_obj.batchMeta);
    return _obj;
  };
  const getBenchmarksByAgent = (id) => {
    const benchmarks = [];
    const batches = state?.batchLogs
      ?.filter((i) => i?.agentID === id)
      ?.sort((a, b) => (a?._lastChangedAt < b?._lastChangedAt ? 1 : -1));
    for (let _obj of batches) {
      if (typeof _obj?.batchConfig == "string")
        _obj.batchConfig = JSON.parse(_obj.batchConfig);
      if (typeof _obj?.batchMeta == "string")
        _obj.batchMeta = JSON.parse(_obj.batchMeta);
      const benchmarkData = state?.benchmarks?.find(
        (bench) => bench?.id == _obj?.benchmarkID
      );
      benchmarks.push({
        batch: _obj,
        benchmark: benchmarkData,
      });
    }

    return benchmarks;
  };
  const getTrainingLogsWithModels = (type) => {
    return state?.trainingLogs?.filter(
      (u) =>
        u?.type === type &&
        u?.status === "completed" &&
        u?.trainingConfig?.datasetOnly !== true
    );
  };
  const generateBaseWorkspaceTrainingConfig = (subscriptionType) => {
    return {
      enableManagedSpotTraining: true,
      includeReadComprehensionDataInTraining: true,
      patienceNLP: 1600.0,
      maxQueueWaitTime: 360,
      maxStepsNLP: 40000.0,
      trainingEpochsNLP: 200.0,
      learningRateNLP: 0.001,
      datasetTestSizeThresholdNLP: 50.0,
      // 0.01 is 50x the safe QLoRA band for an 8B model (5e-4) and diverged
      // runs: grad_norm 52.5, loss 23.30 within 13 optimizer steps. The safe
      // default matches the backend's _LR_DEFAULT and the 8B SFT band.
      learningRateRC: 0.0002,
      // 20 epochs overfits small datasets; 3 is the documented starting point.
      trainingEpochsRC: 3.0,
      trainingIterations: 5.0,
      batchSizeNLP: 8,
      batchSizeRC: 8,
      testSizeRC: 0.2,
      testSizeNLP: 0.2,
      enableManagedSpotTraining: subscriptionType === "base" ? true : false,
      maxRuntimeWaitTime: 86400,
      maxRuntime: 86400,
      generateDockerImageForRCengine: false,
      generateDockerImageForNLPengine: false,
      emailsListForTrainingConfirmation: [],
    };
  };
  const generateAdaptedTopics = (topics) => {
    const adaptedTopics = [];
    for (let i = 0; i < topics.length; i++) {
      let newTopic = topics[i];
      if (newTopic) {
        adaptedTopics.push({
          ...newTopic,
          name: topics[i].label,
          category: topics[i].type,
        });
      }
    }
    return adaptedTopics;
  };
  // CONTINUE
  const generateWorkspaceParsingConfig = (subscriptionType) => {
    return {
      answerAsNER: true,
      answerByParagraph: false,
      isImplicitSelfEnabled: false,
      isSentenceSplitEnabled: false,
      questionsLimit: 10.0,
      uniqueAnswer: true,
    };
  };
  const getTypeFromTrainingType = (trainingType, trainingTypeCode = "gn") => {
    if (trainingType === "nlpTraining" || trainingType === "nlp") return "NLP";
    else if (
      trainingType === "readComprehensionTraining" ||
      trainingType === trainingTypeCode
    )
      return trainingTypeCode.toUpperCase();
    else if (trainingType === "generativeTraining" || trainingType === "gn")
      return "GN";
    else if (trainingType === "conversationalTraining" || trainingType === "cn")
      return "CN";
    else if (trainingType === "grpoTraining" || trainingType === "grpo")
      return "GRPO";
    else if (trainingType === "ktoTraining" || trainingType === "kto")
      return "KTO";
    else if (trainingType === "continuedPretraining" || trainingType === "cpt")
      return "CPT";
    else return "RC";
  };
  const generateWorkspaceNotificationConfig = (subscriptionType) => {
    return {
      emailOnTrainingCompleted: true,
      emailOnTrainingFailed: true,
      emailOnTrainingStart: false,
      notifyAllUsersInWorkspace: false,
      notificationThresholdConsumption: 90.0,
      notifyOnThreasholdConsumption: true,
      emailsList: [],
    };
  };
  const getPlanConfig = () => {
    return reservedConfigs()?.plans
      ? reservedConfigs().plans?.find(
          (u) => u.type === getActiveWorkspace()?.subscriptionType
        )
      : null;
  };
  const getUoiBudget = () => {
    const subscriptionType = getActiveWorkspace()?.subscriptionType;
    const customUoiBudget = getActiveWorkspace()?.customUoiBudget;

    switch (subscriptionType) {
      case "base":
        return 500;
      case "pro":
        return 1500 * Math.max(getActiveWorkspace()?.maxUsers || 1);
      case "business":
        return 5000 * Math.max(getActiveWorkspace()?.maxUsers || 5);
      case "enterprise":
        if (customUoiBudget && customUoiBudget > 0) {
          return customUoiBudget;
        }
        return 7500 * Math.max(getActiveWorkspace()?.maxUsers || 10);
      default:
        return 500;
    }
  };
  const getPromptsByAgent = (agentId) => {
    let mergedItems = [
      {
        id: "generation",
        name: "Manual",
        isCustom: false,
      },
    ];
    if (state?.customPrompts?.length > 0) {
      const agentPrompts = state?.customPrompts?.filter((u) =>
        u?.availableToAgents?.includes(agentId)
      );
      mergedItems = [
        ...mergedItems,
        ...agentPrompts.map((u) => {
          return {
            id: u.type,
            name: u?.label?.charAt(0).toUpperCase() + u?.label?.slice(1),
            isCustom: true,
          };
        }),
      ];
    }
    return mergedItems;
  };
  const loadAgents = async (workspaceID) => {
    let resultAgents = await Agents.getBaseAgentsByWorkspaceID(workspaceID);
    if (isUserAdmin()) {
      resultAgents = resultAgents?.data?.agentsByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );
    } else {
      resultAgents = resultAgents?.data?.agentsByWorkspace?.items?.filter(
        (agent) =>
          agent.workspaceID === workspaceID &&
          ((agent.restrictedAccess === true &&
            agent.restrictedAccessUsers?.includes(user.id)) ||
            agent.restrictedAccess !== true)
      );
    }
    const _cleanAgents = resultAgents
      ?.filter((u) => u?._deleted != true && u?.agentType == "Chatbot")
      ?.sort((a, b) => (a?.label > b?.label ? 1 : -1));
    dispatch("SET_AGENTS", _cleanAgents);

    // Send programmer agents (coder mode) to Electron main process for tfcode
    if (
      typeof window !== "undefined" &&
      window.electronAPI?.setProgrammerAgents
    ) {
      const programmerAgents =
        _cleanAgents?.filter((a) => a?.mode === "coder") ?? [];
      window.electronAPI.setProgrammerAgents(programmerAgents);
    }

    return _cleanAgents;
  };
  const agentWidgetsAvailable = async (workspaceID, agentId) => {
    let resultAgents = await Agents.getBaseAgentsByWorkspaceID(workspaceID);

    resultAgents = resultAgents?.data?.agentsByWorkspace?.items?.filter(
      (u) =>
        u?._deleted != true &&
        u?.preventWidgetUsage == false &&
        u?.id != agentId
    );
    return resultAgents?.length;
  };
  const handleGenerationOfCustomPrompt = async (
    value,
    availableToAgents,
    isEditor = true
  ) => {
    try {
      let mergedItems = [
        ...state?.customPrompts.map((item) => ({ ...item, isCustom: true })),
      ];
      promptData = mergedItems.find(
        (u) => u.type?.toString() === prompt?.toString()
      );
      let _interpolationString;
      if (isEditor)
        _interpolationString =
          AppQuantumEditorUtils.Helpers.getPlainText(value);
      else {
        _interpolationString = value;
      }
      if (!_interpolationString || _interpolationString?.length === 0) {
        return AppToasty.show(t("toasts.noPromptToSave"), {
          type: "warning",
        });
      }
      const _type = generateRowId()?.toString();
      const _promptCode = `${generateRowId()?.toString()}`;
      let newPrompt = {
        type: _type,
        label:
          _interpolationString?.length > 20
            ? _interpolationString?.substring(0, 20) + "..."
            : _interpolationString,
        promptLength: 256,
        scope: _promptCode,
        type: _promptCode,
        interpolationString: _interpolationString,
        workspaceID: getActiveWorkspace()?.id,
        availableToAgents,
      };
      if (state.customPrompts?.find((item) => item.type === _promptCode)) {
        return AppToasty.show(t("toasts.promptCreationFailedDuplicated"), {
          type: "danger",
        });
      }
      const _newRole = await Prompts.createPrompt(newPrompt);
      _lastPrompt = prompt;
      dispatch("SET_CUSTOM_PROMPTS", [...state.customPrompts, _newRole]);
      AppToasty.show(t("toasts.promptCreated"), {
        type: "success",
        position: "bottom",
      });
      // }
      // else {
      //   const _editedRole = await Prompts.updatePrompt(promptData.id, [
      //     {
      //       property: "interpolationString",
      //       value: _interpolationString,
      //     },
      //   ]);
      //   let bufferPrompts = [...state.customPrompts];
      //   const selectedIndex = bufferPrompts.findIndex(
      //     (u) => u.id === promptData?.id
      //   );
      //   bufferPrompts[selectedIndex] = _editedRole;
      //   dispatch("SET_CUSTOM_PROMPTS", [...bufferPrompts]);
      //   AppToasty.show(t("promptUpdateSuccessCaption"), {
      //     type: "success",
      //     position: "bottom",
      //   });
      // }
    } catch (error) {
      console.error("", error);
      AppToasty.show(t("promptCreationErrorCaption"), {
        type: "danger",
      });
    }
  };
  const loadChats = async (showWidget = false, forceCleanup = false) => {
    setIsChatHistoryLoaded(false);
    let _loadMoreChatsToken = loadMoreChatsToken;
    if (forceCleanup) {
      setLoadMoreChatsToken(null);
      _loadMoreChatsToken = null;
    }
    const isAppend = _loadMoreChatsToken != null;
    let resultChats = null;
    if (!isConnectedToCloud()) {
      resultChats = await DataStore.query(Chat, (e) =>
        e.workspaceID("eq", getActiveWorkspace()?.id)
      );
      resultChats = {
        data: {
          chatsByWorkspace: {
            items: resultChats,
          },
        },
      };
    } else {
      resultChats = await chatsHelper.getChats(
        state?.activeWorkspaceID,
        _loadMoreChatsToken,
        showWidget
      );
    }
    if (!resultChats?.data?.chatsByWorkspace?.nextToken && isAppend) {
      dispatch("SET_CHATS_LOADED", true);
      return state?.chats;
    } else if (!resultChats?.data?.chatsByWorkspace?.nextToken)
      dispatch("SET_CHATS_LOADED", true);
    else {
      dispatch("SET_CHATS_LOADED", false);
    }
    setLoadMoreChatsToken(resultChats?.data?.chatsByWorkspace?.nextToken);
    resultChats = resultChats?.data?.chatsByWorkspace?.items;
    resultChats = resultChats?.map((u) => {
      return {
        ...u,
        topics:
          u?.topics && typeof u?.topics == "string"
            ? JSON.parse(u?.topics)
            : [],
      };
    });
    resultChats = resultChats?.filter((u) => {
      if (u?._deleted === true) return false;
      // All users can see public, widget, and job chats
      if (
        u?.visibility === "public" ||
        u?.visibility === "widget" ||
        u?.visibility === "job" ||
        !u?.visibility
      )
        return true;
      // Incognito chats only visible to creator
      if (u?.visibility === "incognito" && u?.createdBy === user?.id)
        return true;
      // Private chats visible to creator or AI Engineer
      if (
        u?.visibility === "private" &&
        (u?.createdBy === user?.id || isUserAIEngineer())
      )
        return true;
      return false;
    });
    if (Constants.showProgrammaticChats === false)
      resultChats = resultChats.filter((u) => u.name);

    const finalChats = isAppend
      ? [...state?.chats, ...resultChats]
      : resultChats;
    // ensure chats have unique ids
    const uniqueChats = finalChats.filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i
    );
    dispatch("SET_CHATS", uniqueChats);
    setIsChatHistoryLoaded(true);
    return uniqueChats;
  };
  const getChat = (id) => {
    return state?.chats?.filter((u) => u?.id === id)[0];
  };
  const getBaseWorkspaceConfig = async (type = "base", domain = "default") => {
    if (!reservedConfigs()?.workspaceBaseConfigs)
      throw new Error("No configuration data found");
    let baseConfig =
      reservedConfigs()?.workspaceBaseConfigs &&
      reservedConfigs()?.workspaceBaseConfigs.find(
        (u) => u.type === type && u.planConfig?.domain === domain
      );
    if (!baseConfig)
      baseConfig = reservedConfigs()?.workspaceBaseConfigs.find(
        (u) => u.type === type
      );
    if (baseConfig) {
      return makeGraphQLRequest(
        graphqlOperation(getSubscriptionBaseConfig, {
          id: baseConfig.id,
        })
      );
    } else {
      throw new Error("No base config found");
    }
  };
  return {
    isTrainingEnabled:
      getPlanConfig()?.isTrainingNLPEnabled ||
      getPlanConfig()?.isTrainingQAEnabled ||
      getPlanConfig()?.isTrainingGNEnabled,
    agcEnabled:
      getPlanConfig()?.agcEnabled &&
      getActiveWorkspace()?.parsingConfig?.agcEnabled,
    getPlanConfig,
    getBaseWorkspaceConfig,
    getActiveWorkspace,
    getEffectiveSubscriptionType,
    updateWorkspaceInContext,
    getLastWorkspaceModelMeta,
    isNLPModelTrained,
    getLastCompletedTrainingLog,
    getWorkspaceBaseModel,
    getWorkspaceEntities,
    getAppLogs,
    handleTabSelection,
    getLastTabSelectedByScreen,
    handleSectionSelection,
    getLogicalTabSelection,
    getRawTabSelection,
    isWorkspaceEnabled,
    isTrainingLogIdActive,
    getLastWorkspaceReadingComprehensionMeta,
    isUserAdmin,
    isUserLoadedInWorkspace,
    returnCleanedWorkspaces,
    getDisabledWorkspaces,
    generateBaseWorkspaceTrainingConfig,
    isRCModelTrained,
    getLastOptionSelectedByScreen: () => {},
    generateWorkspaceParsingConfig,
    generateWorkspaceNotificationConfig,
    getTrainingLogInfo,
    generateAdaptedTopics,
    getActiveTrainingLog,
    isBatchEnabled,
    isModelDownloadEnabled,
    getMaxUsers,
    getUoiBudget,
    isWorkspaceOnDemand,
    isPriorityTrainingEnabled,
    isTF_API_AccessEnabled,
    getTypeFromTrainingType,
    getContentMultiplier,
    getWorkspaceUsers,
    generatorConfigData: state?.customPrompts,
    generatorConfigInProgress,
    getUserName,
    isWorkspaceDisabled,
    isWorkspaceInCreation,
    isGNModelTrained,
    isUserRoot,
    isUserAIEngineer,
    isUserContentAdmin,
    isToothFairyStaff,
    getTrainingLogsWithModels,
    loadChats,
    getAgent,
    getChat,
    canUpdateSubscription,
    isChatHistoryLoaded,
    setIsChatHistoryLoaded,
    handleGenerationOfCustomPrompt,
    getPromptsByAgent,
    getAgents,
    loadAgents,
    getFinetunedModels,
    getBatchLogInfo,
    getBenchmarksByAgent,
    agentWidgetsAvailable,
    mapModeWithLabel,
    getMaxPowerUsers,
    isPowerUserType,
    getPowerUsersCount,
    getRegularUsersCount,
    canAddUserOfType,
  };
};

export default useWorkspaces;
